import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { tokens } from '../../../../global/theme/tokens';
import DateLabel from '../../../label/DateLabel';
import UserStack from '../../UserStack';
import {
  getPriorityColorMap,
  priorityLabelMap,
} from '../../../special/taskPriority';
import { checkDeadlinePassed } from '../../../special/checkDeadlinePassed';

const Row = ({ label, children, alignTop }) => (
  <Box className={`flex gap-3 ${alignTop ? 'items-start' : 'items-center'}`}>
    <Typography
      fontSize="12px"
      className="opacity-60 shrink-0"
      sx={{ width: '90px', lineHeight: '24px' }}
    >
      {label}
    </Typography>
    <Box className="flex flex-col gap-1 grow min-w-0">{children}</Box>
  </Box>
);

const TaskCardSidebar = ({ task, workPackage, onEdit, onMove, onDelete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);

  function handleMenuItem(action) {
    setAnchorEl(null);
    if (action) action(task);
  }

  const priorityColorMap = getPriorityColorMap(colors);
  const deadlinePassed = task.deadline && checkDeadlinePassed(task.deadline);
  const isDone = Boolean(task.status);

  const menuItems = [
    {
      key: 'edit',
      label: t('Edit'),
      icon: <EditOutlinedIcon fontSize="small" />,
      action: onEdit,
    },
    // {
    //   key: 'move',
    //   label: t('Move'),
    //   icon: <DriveFileMoveOutlinedIcon fontSize="small" />,
    //   action: onMove,
    // },
    {
      key: 'delete',
      label: t('Delete'),
      icon: <DeleteOutlineIcon fontSize="small" />,
      action: onDelete,
      color: colors.error,
      dividerBefore: true,
    },
  ];

  return (
    <Box
      className="flex flex-col gap-3 p-4 shrink-0 w-full md:w-[290px]"
      sx={{
        borderLeft: { md: `1px solid ${colors.text}15` },
        // on mobile the sidebar sits above the content, so it separates below
        borderBottom: { xs: `1px solid ${colors.text}15`, md: 'none' },
      }}
    >
      <Box className="flex justify-end -mt-2 -mr-2">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            borderRadius: '6px',
            backgroundColor: anchorEl ? colors.bgInput : 'transparent',
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          // the global MuiBackdrop override blurs and dims everything behind
          // any modal - the card has to stay readable while the menu is open
          sx={{
            '.MuiBackdrop-root': {
              bgcolor: 'transparent',
              background: 'unset',
              backdropFilter: 'unset',
            },
          }}
          slotProps={{
            //menu items css
            paper: {
              sx: {
                minWidth: '170px',
                borderRadius: '12px',
                padding: '4px',
                backgroundColor: colors.glassSolid,
                backgroundImage: 'unset',
                border: `1px solid ${colors.text}15`,
                '& .MuiMenuItem-root': {
                  fontSize: '14px',
                  gap: 1.2,
                  borderRadius: '8px',
                  paddingY: '8px',
                  border: 'none !important',
                },
                '& .MuiListItemIcon-root': { minWidth: 'unset' },
                '& .MuiMenu-list': { paddingY: 0 }, //padding top and bottom of the whole menulist
              },
            },
          }}
        >
          {menuItems.map(
            ({ key, label, icon, action, color, dividerBefore }) => {
              const menuItem = (
                <MenuItem
                  key={key}
                  onClick={() => handleMenuItem(action)}
                  sx={{
                    color: color || 'inherit',
                    ':hover': {
                      backgroundColor: `${color || colors.info}20 !important`,
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: color || 'inherit' }}>
                    {icon}
                  </ListItemIcon>
                  {label}
                </MenuItem>
              );

              // a real Divider instead of the MenuItem "divider" prop: that prop
              // draws a border on the item, which the reset above removes
              return dividerBefore
                ? [
                    <Divider
                      key={`${key}-divider`}
                      sx={{ marginY: '4px', marginX: '8px' }}
                    />,
                    menuItem,
                  ]
                : menuItem;
            },
          )}
        </Menu>
      </Box>

      <Row label={t('Status')}>
        <Box className="flex items-center gap-2">
          <Box
            className="rounded-full"
            sx={{
              width: '8px',
              height: '8px',
              backgroundColor: isDone ? colors.success : `${colors.text}50`,
            }}
          />
          <Typography fontSize="13px">
            {isDone ? t('Done') : t('Open')}
          </Typography>
        </Box>
      </Row>

      <Row label={t('Priority')}>
        <Box className="flex items-center gap-1">
          <KeyboardDoubleArrowUpIcon
            sx={{
              fontSize: '16px',
              color: priorityColorMap[task.priority],
              transform: `rotate(${task.priority === 1 ? 180 : 0}deg)`,
            }}
          />
          <Typography fontSize="13px">
            {t(priorityLabelMap[task.priority])}
          </Typography>
        </Box>
      </Row>

      {task.effortScore != null && (
        <Row label={t('Points')}>
          <Typography fontSize="13px" fontWeight={500}>
            {`${task.effortScore} ${t('Points')}`}
          </Typography>
        </Row>
      )}

      {task.deadline && (
        <Row label={t('Due')}>
          <Typography
            fontSize="13px"
            sx={{ color: deadlinePassed && !isDone ? colors.error : 'inherit' }}
          >
            <DateLabel date={task.deadline} />
          </Typography>
        </Row>
      )}

      {task.users?.length > 0 && (
        <Row label={t('Assignee')}>
          {/* avatars only - a long assignee list would blow up the sidebar.
              Clicking the stack opens the list with the names */}
          <Box className="flex">
            <UserStack users={task.users} max={4} />
          </Box>
        </Row>
      )}

      {/* the task only carries workPackageId, so the package itself comes
          from the list that renders the card */}
      {workPackage && (
        <Row label={t('Work Package')} alignTop>
          {workPackage.nr && (
            <Typography fontSize="13px">{workPackage.nr}</Typography>
          )}
          {/* the detail panel shows the whole title, so it wraps instead of
              being cut off - a long one without spaces would overflow it */}
          <Typography
            fontSize="13px"
            className="opacity-70"
            sx={{ wordBreak: 'break-word' }}
          >
            {workPackage.title}
          </Typography>
        </Row>
      )}

      {task.workPackageListElement && (
        <Row label={t('List')} alignTop>
          <Typography fontSize="13px">
            {task.workPackageListElement.name}
          </Typography>
        </Row>
      )}

      {task.createdAt && (
        <Row label={t('Created')}>
          <Typography fontSize="13px">
            <DateLabel date={task.createdAt} />
          </Typography>
        </Row>
      )}
    </Box>
  );
};

export default TaskCardSidebar;
